import "./DatePickerBlock.css";

import React, { useEffect, useImperativeHandle, useState } from "react";
import { clsx } from "clsx";
import dayjs from "dayjs";
import { CalendarOutlined } from "@ant-design/icons";
import { FormattedMessage } from "react-intl";
import { DatePicker, Popover, Typography } from "antd";
import { PickerRef } from "rc-picker";

import { BrowserDetection } from "scripts/infrastructure/helpers/browser";
import { FieldLabel } from "@ui-components/FieldLabel";
import { Periods } from "@binale-tech/shared";

export interface Props {
    value: Date;
    yearBound: number;
    periodBound?: number;
    disabled?: boolean;
    onChange?: (date: Date) => void;
    hint?: React.ReactNode;
}

export const DatePickerBlock = function DatePickerBlock({
    ref,
    ...props
}: Props & {
    ref?: React.RefObject<PickerRef>;
}) {
    const hasPeriodBound = Number.isFinite(props.periodBound);
    const dateFormat = hasPeriodBound ? "DD" : "DD.MM.";

    const [showPopover, setShowPopover] = useState(false);

    const pickerRef = React.useRef<PickerRef>(null);
    useImperativeHandle(ref, () => pickerRef?.current, [pickerRef]);

    const max = React.useMemo(() => {
        const month = hasPeriodBound ? Periods.getMonthAndDay(props.periodBound).month : 11;
        return dayjs(new Date(props.yearBound, month)).endOf("month").toDate();
    }, [hasPeriodBound, props.periodBound, props.yearBound]);

    const min = React.useMemo(() => {
        const month = hasPeriodBound ? Periods.getMonthAndDay(props.periodBound).month : 0;
        return new Date(props.yearBound, month, 1);
    }, [hasPeriodBound, props.periodBound, props.yearBound]);

    const isDateInRange = (date: dayjs.Dayjs) => {
        return (
            date.isValid() &&
            (date.isBefore(max) || date.isSame(max, "day")) &&
            (date.isAfter(min) || date.isSame(min, "day"))
        );
    };

    const handleChange = (date: dayjs.Dayjs | null) => {
        if (date && !date.isSame(dayjs(props.value), "day")) {
            if (isDateInRange(date)) {
                props.onChange?.(date.toDate());
            } else {
                setShowPopover(true);
                setTimeout(() => pickerRef.current?.focus(), 100);
            }
        }
    };

    const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const tv = e.target.value;
        if (typeof tv === "undefined") {
            return;
        }
        if (!tv?.length) {
            return;
        }
        const ddmm = hasPeriodBound ? `${tv.padStart(2, "0")}.${dayjs(min).format("MM")}` : tv;
        const ddmmyyyy = String(ddmm + "." + props.yearBound).replaceAll("..", ".");
        const parsed = dayjs(ddmmyyyy, "DD.MM.YYYY", true);

        if (isDateInRange(parsed)) {
            handleChange(parsed);
            return;
        }
        setShowPopover(true);
        setTimeout(() => pickerRef.current?.focus(), 100);
    };

    // const onCalendarClick = () => {
    //     pickerRef.current.setOpen(true);
    // };

    const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        const input = e.target;
        e.stopPropagation();
        setTimeout(() => {
            input.selectionStart = 0;
            input.selectionEnd = 2;
        }, 100);
    };

    useEffect(() => {
        if (showPopover) {
            setTimeout(() => {
                setShowPopover(false);
            }, 5000);
        }
    }, [showPopover]);

    // that hack needs to focus next form element instead of buttons in calendar
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const target = e.target as HTMLDivElement;
        if (target.tagName.toUpperCase() === "INPUT" && e.key === "Enter") {
            e.preventDefault();
            return;
        }
        if (e.altKey || e.ctrlKey || e.metaKey || e.key.startsWith("Arrow")) {
            return;
        }
        if (e.key === "Backspace" || e.key === "Delete") {
            return;
        }
        const pattern = dateFormat === "DD" ? /[0-9]/ : /[0-9.]/;
        if (!pattern.test(e.key)) {
            e.preventDefault();
            return;
        }
    };

    const renderSuffix = () => (
        <>
            {hasPeriodBound && (
                <span className="DatumPickerBlock__suffixMonth" data-testid="date-picker-suffix-month">
                    {dayjs(props.value).format(".MM.")}
                </span>
            )}
            <CalendarOutlined />
        </>
    );

    return (
        <div onKeyDown={handleKeyDown}>
            <FieldLabel
                label={<FormattedMessage id="app.fields.date" />}
                labelAddon={<Typography.Text type="secondary">{props.hint}</Typography.Text>}
                style={{ minWidth: 90, width: 90 }}
                className={clsx("DatumPickerBlock", {
                    "DatumPickerBlock--safari": BrowserDetection.isSafari,
                    "DatumPickerBlock--hasPeriodBound": hasPeriodBound,
                })}
            >
                <Popover
                    open={showPopover}
                    placement="topLeft"
                    onOpenChange={() => setShowPopover(false)}
                    title={<FormattedMessage id="app.components.date.range_error" />}
                    content={
                        <strong>
                            {dayjs(min).format("DD.MM.YYYY")}
                            {" - "}
                            {dayjs(max).format("DD.MM.YYYY")}
                        </strong>
                    }
                >
                    <DatePicker
                        ref={pickerRef}
                        value={dayjs(props.value)}
                        onChange={handleChange}
                        format={dateFormat}
                        placeholder={""}
                        disabledDate={date => date.isBefore(min) || date.isAfter(max)}
                        disabled={
                            props.disabled ||
                            props.periodBound === Periods.Period.FirstDayOfYear ||
                            props.periodBound === Periods.Period.LastDayOfYear
                        }
                        onKeyDown={handleKeyDown}
                        placement="topLeft"
                        suffixIcon={renderSuffix()}
                        allowClear={false}
                        showToday={false}
                        onFocus={e => {
                            handleOnFocus(e as any);
                        }}
                        onBlur={(e, info) => {
                            onBlur(e as any);
                        }}
                    />
                </Popover>
            </FieldLabel>
        </div>
    );
};
